import { ACCREDITATION_FIELDS } from '@/entities/organization/units/constants'

// Blank check for names, CLIA license, and other plain text fields.
// Do NOT apply ETL accreditation flag rules here — "0" / "false" can be real text.
export const isBlankIdentifier = (value: unknown): boolean => {
  if (value == null || value === false) {
    return true
  }
  if (typeof value === 'string') {
    return value.trim() === ''
  }

  return false
}

// ETL fold_accreditation may persist boolean true/false (legacy 1/0 flags).
// Those flag values are not real accreditation identifiers.
export const isBlankAccreditationValue = (value: unknown): boolean => {
  if (value == null || value === false) {
    return true
  }
  if (value === true) {
    return false
  }
  if (typeof value === 'number') {
    return value === 0
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed === '' || ['false', '0'].includes(trimmed.toLowerCase())
  }

  return String(value).trim() === ''
}

export const identifierInputValue = (value: unknown): string => {
  if (isBlankAccreditationValue(value)) {
    return ''
  }
  if (value === true) {
    // Legacy 1/0 accreditation flags — keep a non-blank token so edit/save works.
    return '1'
  }
  if (typeof value === 'string') {
    return value.trim()
  }

  return String(value)
}

const toCamelCaseKey = (key: string): string =>
  key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())

export const normalizeAccreditationForForm = (
  accreditation: Record<string, unknown> | null | undefined,
): Record<string, string> => {
  if (!accreditation) {
    return {}
  }

  return Object.fromEntries(
    Object.entries(accreditation).map(([key, value]) => [
      toCamelCaseKey(key),
      identifierInputValue(value),
    ]),
  )
}

const toSnakeCaseKey = (key: string): string =>
  key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)

export const snakeCaseAccreditation = (
  accreditation: Record<string, string>,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(accreditation)
      .map(([key, value]): [string, string] => [
        toSnakeCaseKey(key),
        typeof value === 'string' ? value.trim() : String(value ?? '').trim(),
      ])
      // Drop blank entries so empty inputs do not overwrite ETL false flags with "".
      .filter(([, value]) => value !== ''),
  )

export const hasLicenseOrAccreditationValues = (
  licenseNumber: unknown,
  accreditation: Record<string, unknown>,
): boolean => {
  if (!isBlankIdentifier(licenseNumber)) {
    return true
  }

  return ACCREDITATION_FIELDS.some(({ key }) => {
    const snakeCaseKey = toSnakeCaseKey(key)
    return (
      !isBlankAccreditationValue(accreditation[key]) ||
      !isBlankAccreditationValue(accreditation[snakeCaseKey])
    )
  })
}
